import React, { useRef, useCallback } from 'react';
import { isC2ContainerComponent, isC2RootContainerComponent, isColumnComponent, isRootComponent } from './utils/SmartLayoutUtils.js';
import { classNames } from 'primereact/utils';
import './Designable.scss.js';
import { useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { useCombinedRefs } from '@dnd-kit/utilities';
import { DesignActionsContainer } from './DesignActionsContainer.js';
import { createPortal } from 'react-dom';

const Designable = ({ isSelected, handleEdit, handleDelete, handleUngroup, handleClone, onColumnOperation, meta, index, className, children, componentList, designMode, multipleSelection, onEngineClicked }) => {
    var _a;
    const isContainer = isC2ContainerComponent(meta) || isC2RootContainerComponent(meta);
    const containerRef = useRef(null);
    const { attributes, listeners, setNodeRef, transform, active, isDragging } = useDraggable({
        id: meta.id,
        data: {
            meta,
            index,
            isMoveOperation: true,
        },
    });
    const { setNodeRef: droppableRef, isOver } = useDroppable({
        id: meta.id,
        data: {
            meta,
        },
        disabled: meta.id === "rootDiv" || meta.id === "rootContainer" || meta.type === "C2Columns",
    });
    const onDesignableClick = (event) => {
        /*
        if (isRootComponent(meta, componentList)) {
          handleEdit(null);
          return;
        }*/
        event.stopPropagation();
        handleEdit(meta, event);
        onEngineClicked();
    };
    let clazzName = classNames({
        "comp-designable-container": true,
        "flex": isColumnComponent(meta),
        "bg-white-0": false ,
        selected: isSelected,
        //"w-full": isRootComponent(meta, componentList),
        "h-full": isRootComponent(meta, componentList),
        [className]: true,
        "bg-blue-600": isOver,
        "z-2": isSelected,
        "p-1": meta.id === "rootContainer"
    });
    const opacityClasses = {
        "opacity-0": active && meta.id === active.id,
        "opacity-100": !active || meta.id !== active.id,
    };
    if (meta.type === "C2Column") {
        const colClass = meta.props.cols ? `col-${meta.props.cols}` : "col";
        clazzName = classNames({
            [colClass]: true,
            "p-0": true,
            "w-full": false
        }, clazzName);
    }
    const elementStyle = Object.assign({}, (_a = meta.props.style) !== null && _a !== void 0 ? _a : {});
    const style = transform ? Object.assign({ transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, padding: 0 }, elementStyle) : Object.assign({ zIndex: 1 }, elementStyle);
    const dropStyle = {
        "bg-blue-600": isOver,
        "opacity-50": isOver,
    };
    const dropSpanStyle = meta.type === "C2Container" ? {
        display: "inherit",
        opacity: "1 !important",
        width: "inherit",
        "border-radius": "inherit",
        "border-width": "inherit",
        "border-style": "inherit",
        gap: "inherit",
        "min-height": "inherit",
        "flex-wrap": "inherit",
        "align-items": "inherit",
        "flex-direction": "inherit"
    } : { opacity: "1 !important" };
    const combinedRefs = useCombinedRefs(setNodeRef, droppableRef);
    const findDesigner = (element) => {
        var _a, _b;
        if ((element === null || element === void 0 ? void 0 : element.parentElement) && ((_a = element.parentElement.attributes["data-testid"]) === null || _a === void 0 ? void 0 : _a.value) === "designer") {
            return element.parentElement.getBoundingClientRect();
        }
        return findDesigner((_b = element === null || element === void 0 ? void 0 : element.parentElement) !== null && _b !== void 0 ? _b : null);
    };
    const whereToShow = (designerRect, containerRect) => {
        if (containerRect.top >= designerRect.height ||
            (designMode === "basic" && containerRect.top + containerRect.height >= designerRect.height + designerRect.top) ||
            (designMode === "advanced" && containerRect.top + containerRect.height + 12 >= designerRect.height + designerRect.top)) {
            return "top";
        }
        return "bottom";
    };
    const actionContainerPosition = useCallback(() => {
        if (containerRef.current && isSelected) {
            const designerBoundingRect = findDesigner(containerRef.current);
            const containerRefBoundingRect = containerRef.current.getBoundingClientRect();
            const showOn = whereToShow(designerBoundingRect, containerRefBoundingRect);
            const left = containerRefBoundingRect.left ? containerRefBoundingRect.left + (containerRefBoundingRect.width / 2) : 0;
            const top = showOn === "bottom" ? (containerRefBoundingRect.top ? containerRefBoundingRect.top + containerRefBoundingRect.height : 0) : meta.id === "container" ? (containerRefBoundingRect.bottom ? containerRefBoundingRect.bottom - 32 : 0) : (containerRefBoundingRect.top ? containerRefBoundingRect.top - 36 : 0);
            const bottom = containerRefBoundingRect.top ? containerRefBoundingRect.top + 16 : 0;
            return { top, left, bottom };
        }
        return { top: 0, left: 0, bottom: 0 };
    }, [containerRef, isSelected]);
    return designMode === "advanced" ? (!isContainer ? (React.createElement(React.Fragment, null,
        React.createElement(DragOverlay, { style: { cursor: "move" } }, active && meta.id === active.id ? React.createElement("div", { className: clazzName }, children) : null),
        React.createElement("div", { className: classNames(clazzName, Object.assign(Object.assign({}, opacityClasses), dropStyle)), ref: combinedRefs, style: style, onClick: onDesignableClick, "data-key": `${meta.id}-${meta.name}` },
            isSelected && createPortal(React.createElement(DesignActionsContainer, { listeners: listeners, attributes: attributes, isRootComponent: isRootComponent(meta, componentList), meta: meta, onColumnOperation: onColumnOperation, onClone: handleClone, onDelete: handleDelete, onUngroup: handleUngroup, positions: actionContainerPosition() }), document.body),
            React.createElement("span", { ref: containerRef }, children)))) : (React.createElement(React.Fragment, null,
        React.createElement(DragOverlay, { style: { cursor: "move" } }, active && meta.id === active.id ?
            React.createElement("div", { className: clazzName, "data-type": "sortable-droppable" }, children) : null),
        React.createElement("div", { className: classNames(clazzName, Object.assign(Object.assign({}, opacityClasses), dropStyle)), ref: combinedRefs, style: Object.assign({ opacity: "1 !important" }, style), onClick: onDesignableClick, "data-key": `${meta.id}-${meta.name}` },
            React.createElement(SortableContext, { items: meta.children.map(item => item.value) },
                React.createElement("span", { className: classNames(dropStyle), ref: containerRef, style: dropSpanStyle, "data-key": `${meta.id}-${meta.name}` },
                    isSelected && createPortal(React.createElement(DesignActionsContainer, { listeners: listeners, attributes: attributes, meta: meta, isRootComponent: isRootComponent(meta, componentList), onColumnOperation: onColumnOperation, onClone: handleClone, onDelete: handleDelete, onUngroup: handleUngroup, positions: actionContainerPosition() }), document.body),
                    children)))))) :
        (React.createElement("div", { className: classNames(clazzName, "h-full overflow-auto"), tabIndex: 0, ref: containerRef, style: style, onClick: onDesignableClick, "data-key": `${meta.id}-${meta.name}` },
            isSelected && !multipleSelection && createPortal(React.createElement(DesignActionsContainer, { designMode: "basic", isRootComponent: isRootComponent(meta, componentList), meta: meta, positions: actionContainerPosition(), onColumnOperation: onColumnOperation, onClone: handleClone, onDelete: handleDelete, onUngroup: handleUngroup }), document.body),
            children));
};

export { Designable };
//# sourceMappingURL=Designable.js.map
